import {
    BuildingOffice2Icon,
    ChatBubbleLeftIcon,
    ClipboardDocumentListIcon,
    EnvelopeIcon,
} from "@heroicons/react/24/outline";
import { WorkflowActionId } from "@sp-crm/core";
import { useQuery } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { ChoiceTileOption } from "components/ui/choice-tiles";
import { PrimaryPlusButton } from "components/ui/primary-plus-button";
import React from "react";
import { handleEvent } from "util/user-events";
import { workflowAnchoredOffsetRecipe } from "../manage-workflows-helper";
import { workflowEventProperties } from "./builder-analytics";
import {
    actionChipKind,
    actionTimelineLabel,
    settingsForAction,
    wizardActionSettings,
    workflowActionSettingSuffix,
    workflowActionTimings,
} from "./builder-helper";
import { WizardStepProps } from "./wizard-types";
import { WorkflowActionCard } from "./workflow-action-card";
import { withActionInterval } from "./workflow-actions";
import {
    actionKey,
    ActionMoveDirection,
    actionOptions,
    withActionAppended,
    withActionKind,
    withActionMoved,
    withActionRemoved,
    WorkflowAction,
} from "./workflow-blueprint";

const NEW_ACTION: WorkflowAction = { kind: "email", audience: "recipients" };

const actionIcon = (key: string, className: string): React.ReactNode => {
    if (key === "task") {
        return <ClipboardDocumentListIcon className={className} />;
    }
    if (key === "sms") {
        return <ChatBubbleLeftIcon className={className} />;
    }
    if (key === "email:unselectedCommunities") {
        return <BuildingOffice2Icon className={className} />;
    }
    return <EnvelopeIcon className={className} />;
};

export const WizardActions: React.FC<WizardStepProps> = props => {
    const {
        workflow,
        workflowSettings,
        editable,
        isDraft,
        canEditStructure,
        onChangeAction,
        editPayload,
        refetch,
        blueprint,
    } = props;

    const actions = workflow.actions ?? [];
    const claims = actions.map(action => ({
        kind: actionChipKind(action.__typename),
        suffix: workflowActionSettingSuffix(action),
    }));
    const tenantSettings = wizardActionSettings(workflow.settings || []);
    const timings = workflowActionTimings(
        workflow,
        workflowAnchoredOffsetRecipe(workflow, workflowSettings),
    );
    const firstEmailActionId =
        actions.find(action => action.__typename === "WorkflowActionEmailEntity")?.id ??
        null;

    const eventProperties = React.useMemo(
        () => workflowEventProperties(workflow, blueprint.blueprint),
        [workflow, blueprint.blueprint],
    );

    const trpcClient = trpc.useTRPC();
    const phoneLinesQuery = useQuery({
        ...trpcClient.telecom.listWorkflowPhoneLines.queryOptions({
            workflowId: workflow.id,
        }),
        enabled: actions.some(action => action.__typename === "WorkflowActionSmsEntity"),
    });
    const phoneLines = phoneLinesQuery.data?.phoneLines ?? [];

    const commitDelayRecipe = React.useCallback(
        (delayRecipe: string) => {
            handleEvent("workflow-builder-edit", {
                ...eventProperties,
                field: "timing",
                delayRecipe,
            });
            return editPayload(payload => ({ ...payload, delay: 0, delayRecipe }));
        },
        [editPayload, eventProperties],
    );

    const commitInterval = React.useCallback(
        (actionId: WorkflowActionId, delayRecipe: string) => {
            handleEvent("workflow-builder-edit", {
                ...eventProperties,
                field: "interval",
                actionId,
                delayRecipe,
            });
            return editPayload(payload =>
                withActionInterval(payload, actionId, delayRecipe),
            );
        },
        [editPayload, eventProperties],
    );

    const reportSettingChange = React.useCallback(
        (settingKey: string) => {
            handleEvent("workflow-builder-setting-change", {
                ...eventProperties,
                settingKey,
            });
            refetch();
        },
        [eventProperties, refetch],
    );

    const actionKindOptions = React.useMemo<ChoiceTileOption<string>[]>(() => {
        if (!blueprint.blueprint) {
            return [];
        }
        return actionOptions(blueprint.blueprint).map(option => ({
            key: option.key,
            title: option.title,
            description: option.description,
            icon: actionIcon(option.key, "w-5 h-5"),
        }));
    }, [blueprint.blueprint]);

    const handleSelectKind = React.useCallback(
        (actionId: WorkflowActionId, key: string) => {
            if (!blueprint.blueprint) {
                return;
            }
            const option = actionOptions(blueprint.blueprint).find(
                candidate => candidate.key === key,
            );
            if (option) {
                blueprint.onApply(
                    withActionKind(blueprint.blueprint, actionId, option.action),
                );
            }
        },
        [blueprint],
    );

    const handleAddAction = React.useCallback(() => {
        if (!blueprint.blueprint) {
            return;
        }
        blueprint.onApply(
            withActionAppended(
                blueprint.blueprint,
                NEW_ACTION,
                blueprint.context.newActionId(),
            ),
        );
    }, [blueprint]);

    const handleMove = React.useCallback(
        (actionId: WorkflowActionId, direction: ActionMoveDirection) => {
            if (!blueprint.blueprint) {
                return;
            }
            blueprint.onApply(withActionMoved(blueprint.blueprint, actionId, direction));
        },
        [blueprint],
    );

    const handleRemove = React.useCallback(
        (actionId: WorkflowActionId) => {
            if (!blueprint.blueprint) {
                return;
            }
            blueprint.onApply(withActionRemoved(blueprint.blueprint, actionId));
        },
        [blueprint],
    );

    return (
        <div className="space-y-6">
            {actions.map((action, position) => {
                const actionId = action.id;
                const blueprintAction =
                    blueprint.blueprint?.actions.find(entry => entry.id === actionId) ??
                    null;
                return (
                    <WorkflowActionCard
                        key={actionId ?? position}
                        workflow={workflow}
                        workflowSettings={workflowSettings}
                        action={action}
                        position={position}
                        actionCount={actions.length}
                        settings={settingsForAction(tenantSettings, claims, position)}
                        timeline={actionTimelineLabel(timings, position)}
                        editable={editable}
                        isDraft={isDraft}
                        canEditStructure={canEditStructure}
                        selectedActionKey={
                            blueprintAction ? actionKey(blueprintAction.action) : null
                        }
                        actionKindOptions={actionKindOptions}
                        showEmailSender={
                            actionId !== null && actionId === firstEmailActionId
                        }
                        phoneLines={phoneLines}
                        phoneLinesLoaded={phoneLinesQuery.isSuccess}
                        onSelectKind={key => {
                            if (actionId) {
                                handleSelectKind(actionId, key);
                            }
                        }}
                        onMove={direction => {
                            if (actionId) {
                                handleMove(actionId, direction);
                            }
                        }}
                        onRemove={() => {
                            if (actionId) {
                                handleRemove(actionId);
                            }
                        }}
                        onCommitTiming={delayRecipe =>
                            position === 0
                                ? commitDelayRecipe(delayRecipe)
                                : actionId
                                  ? commitInterval(actionId, delayRecipe)
                                  : Promise.resolve()
                        }
                        onChangeAction={onChangeAction}
                        onSettingSaved={reportSettingChange}
                    />
                );
            })}
            {canEditStructure ? (
                <div className="rounded-lg border-2 border-dashed border-gray-300 px-4 py-6 text-center">
                    <PrimaryPlusButton onClick={handleAddAction}>
                        Add action
                    </PrimaryPlusButton>
                </div>
            ) : null}
        </div>
    );
};
